import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { savePrefs } from '../../auth/passwordSession';
import { useSession } from '../../auth/useSession';
import { toast } from '../../community/ui/toast';

type SessionLike = { userId?: string | null; prefs?: Record<string, unknown> } | null | undefined;

/** The viewer's yes-or-no preferences kept on the account (server local_auth PREF_KEYS). */
export type AccountChoice = 'viewerSharpness' | 'viewerSharpMotion' | 'viewerXrayAutoWindow';

/** A yes-or-no choice kept on the account when signed in and in this browser either way. */
export function useAccountChoice(key: AccountChoice, local: boolean, setLocal: (v: boolean) => void): [boolean, (v: boolean) => void] {
  const { data: me } = useSession();
  const qc = useQueryClient();
  const pref = me?.prefs?.[key];
  const value = typeof pref === 'boolean' ? pref : local;
  useEffect(() => {
    if (typeof pref === 'boolean' && pref !== local) setLocal(pref);
  }, [pref, local, setLocal]);
  const set = (v: boolean) => {
    // The choice already made: nothing to keep, nothing to send.
    if (v === value) return;
    setLocal(v);
    if (!me?.userId) return;
    // A session request still on its way would land with the old choice and
    // the effect above would put it back: it is called off, the cache takes
    // the new choice, and the server's answer settles it.
    void qc.cancelQueries({ queryKey: ['session'] });
    const withPrefs = (prefs: Record<string, unknown>) =>
      qc.setQueryData<SessionLike>(['session'], (old) => (old ? { ...old, prefs: { ...(old.prefs ?? {}), ...prefs } } : old));
    withPrefs({ [key]: v });
    savePrefs({ [key]: v }).then(withPrefs, () => {
      toast('Your setting could not be saved');
      void qc.invalidateQueries({ queryKey: ['session'] });
    });
  };
  return [value, set];
}
